import { Box, Tooltip } from "@mui/material";
import strings from "global/constants/StringConstants";
import { globalEmitter } from "utils/emitter";
import {
  DateRange,
  Error,
  FormattedResources,
  Resources,
} from "models/interfaces";
import { logOutAction, setLastVisitedLink } from "redux/authSlice";
import { store } from "utils/store";
import history from "utils/history";
import MomentHelpers from "helpers/MomentHelpers";
import moment, { DurationInputArg2 } from "moment-timezone";
import { PhoneNumberUtil } from "google-libphonenumber";
import viewpaths from "global/constants/ViewPathConstants";
import UAParser from "ua-parser-js";
import notifiers from "global/constants/NotificationConstants";
import { IS_EMBED } from "embed/embedMode";

const methodsContext = this;

/**
 * The zone every timestamp crosses the wire in, both directions.
 *
 * The backend switched from Eastern to **UTC**; the string format is unchanged
 * (`MM/DD/YYYY HH:mm:ss` and friends), only the zone it is to be read in. It
 * lives here as one constant rather than a literal repeated across the date
 * helpers so the two directions can never drift apart — reading a timestamp as
 * UTC while still writing one back as Eastern would be silently wrong by the
 * offset, and only for users outside that zone.
 *
 * The `EST` in several helper names below is now historical. Renaming them
 * would churn ~60 call sites for no behaviour change, so the names stayed and
 * this is the single place that says what the wire zone actually is.
 */
export const BACKEND_TIMEZONE = "UTC";

/**
 * The signed callback-auth key for the current signing surface, read from this
 * page's own URL (`?key=`) — minted per-request by the backend, either on the
 * emailed sign link (`DocumentSignHelper.generateSignRequestUrl`) or the
 * in-person hand-off (`InPersonSessionResponse.requestKey`, appended to the
 * `doSign` URL by the in-person launch/advance calls).
 *
 * Every `/callback/...` call this signing surface makes must forward it back,
 * paired with `?id=` set to the same token used in the request path — the
 * backend's signed-token check (`CryptoService.validateClientSecret`) binds
 * the key to that id, so a mismatched or missing one 401s regardless of the
 * key's own validity. There used to be a static, hardcoded fallback key here;
 * it no longer validates against the backend (04a-defD Option C2 replaced the
 * shared secret with per-request signed tokens) and has been removed rather
 * than left as dead, misleading code.
 */
export const getCallbackKeyFromUrl = (): string =>
  new URLSearchParams(window.location.search).get("key") || "";

export const getRequestTokenFromUrl = (): string =>
  new URLSearchParams(window.location.search).get("id") || "";

/**
 * `?id=<token>&key=<link key>` — the query pair every signer `/callback/` call
 * carries (see `getCallbackKeyFromUrl`). Use it for new callback URLs instead
 * of restating the template literal.
 */
export const callbackQuery = (requestToken: string): string =>
  `?id=${encodeURIComponent(requestToken)}&key=${encodeURIComponent(getCallbackKeyFromUrl())}`;

/**
 * Fill `{name}` placeholders in a copy string. Shared by every feature's
 * constants copy (SMS credits, identity verification, …); a placeholder with
 * no value is left visible so a missing value is obvious.
 */
export const fillCopy = (
  template: string,
  values: Record<string, string | number>,
): string =>
  Object.keys(values).reduce(
    (text, key) => text.split(`{${key}}`).join(String(values[key])),
    template,
  );

export const sanitizeSignerEmail = (email: unknown): string => {
  if (email == null || typeof email !== "string") return "";
  return email.replace(/\^@/g, "@").trim();
};

/**
 * Push a GTM `signup_complete` event once a new account is created, tagged with
 * the plan the user signed up with (`plan_id`). Called from every signup entry
 * point (email/password + OAuth). Wrapped in try/catch so analytics can never
 * break the signup flow.
 */
export const trackSignupComplete = (planId?: string) => {
  try {
    const w = window as any;
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({
      event: "signup_complete",
      plan_id: planId,
    });
  } catch {
    // no-op — never let analytics interrupt registration
  }
};

export const isTruthy = (value: any, shouldCheckByType: boolean = true) => {
  const validatedByType = shouldCheckByType
    ? customValidatorByType(value)
    : true;

  if (value !== null && value !== undefined && validatedByType) {
    return true;
  }
  return false;
};

export const hasGoogleAuthProvider = (authProviders: any[]) => {
  return (
    authProviders &&
    authProviders.some(
      (provider) => provider.provider?.toLowerCase() === "googleauth",
    )
  );
};

const customValidatorByType = (value: any) => {
  if (value !== undefined && value !== null) {
    const type = typeof value;
    switch (type) {
      case "string":
        return value.trim() !== "";
      case "object":
        if (Array.isArray(value)) {
          return value.length > 0;
        } else {
          return Object.keys(value).length > 0;
        }
      default:
        return true;
    }
  }
};

export const compareStrings = (string1: string, string2: string) => {
  if (!(isTruthy(string1) || isTruthy(string2))) {
    return true;
  } else {
    if (string1 && string2) {
      if (string1.toLowerCase() === string2.toLowerCase()) {
        return true;
      }
    }
  }
  return false;
};

export const getGreetings = () => {
  const date = new Date();
  let hours = date.getHours();
  let status =
    hours < 12
      ? "Good Morning"
      : hours <= 18 && hours >= 12
        ? "Good Afternoon"
        : "Good Evening";

  return status;
};

export const openInfoNotification = (message: any, title: string = "Info") => {
  globalEmitter.emit(strings.notification, {
    type: strings.info,
    message: message,
    title: title,
  });
};

export const openSuccessNotification = (
  message: any,
  title: string = "Success",
) => {
  globalEmitter.emit(strings.notification, {
    type: strings.success,
    message: message,
    title: title,
  });
};

export const openWarningNotification = (
  message: any,
  title: string = "Warning",
) => {
  globalEmitter.emit(strings.notification, {
    type: strings.warning,
    message: message,
    title: title,
  });
};

/**
 * Reads the HTTP status off a failed `makeCall` error. `makeCall` re-throws the
 * parsed response body and attaches the status as `statusCode` (see
 * `utils/service.ts`), but different layers surface it as `status`/`code`, so
 * callers branching on a specific code should read it through here.
 *
 * Non-numeric values are dropped: `code` is a string on plenty of unrelated
 * error shapes (`"ERR_NETWORK"`), and callers compare against literal statuses.
 */
export const getErrorStatusCode = (error: any): number | undefined => {
  const code = error?.statusCode ?? error?.status ?? error?.code;
  return typeof code === "number" ? code : undefined;
};

/**
 * Shown whenever the request never reached the server — the API is down, the
 * machine is offline, or the call timed out. Says what the user can actually do
 * about it, rather than the old "contact the support team".
 */
export const NETWORK_ERROR_MESSAGE =
  "Can't reach the server. Check your connection and try again.";

/**
 * True when the request never reached the server, as opposed to coming back
 * with a bad status.
 *
 * `fetch` rejects with a `TypeError` whose **`message`** carries the browser's
 * wording. The check this replaces read `errorMessage` — a field only our own
 * thrown objects carry, never a `TypeError` — so it could not match, and an
 * unreachable API fell out of `handleErrorMessage` with no message at all.
 *
 * Matched on the wording rather than on `name === "TypeError"` alone: a
 * TypeError from an ordinary bug ("x is not a function") must keep falling
 * through instead of being mislabelled as an outage. The wording differs per
 * browser, hence the alternation.
 */
export const isNetworkError = (error: any): boolean => {
  if (!error) return false;
  // An HTTP response, however bad, is not a transport failure.
  if (getErrorStatusCode(error) !== undefined) return false;

  const text = String(error?.message ?? error?.errorMessage ?? "");
  return /failed to fetch|networkerror when attempting|load failed|network request failed/i.test(
    text,
  );
};

// The sentence to SHOW for a failed request, where the caller renders the text itself instead of
// raising a toast. Core's 4xx is written for the reader; a 5xx or transport error is not, so the
// caller's own wording is used. Same rule handleErrorMessage applies to toasts.
export const apiErrorMessage = (error: unknown, fallback: string): string => {
  const failure = error as { statusCode?: number; message?: unknown } | null;
  const status = failure?.statusCode;
  const message =
    typeof failure?.message === "string" ? failure.message.trim() : "";
  return status !== undefined && status >= 400 && status < 500 && message
    ? message
    : fallback;
};

/**
 * The API's user-facing message on a rejected call, whichever shape the caller
 * threw: the parsed body (`makeCall`), a bare string (`fetchDocument`), or a
 * nested `error.message`.
 */
export const getErrorMessageText = (error: any): string => {
  const raw =
    typeof error === "string"
      ? error
      : (error?.message ?? error?.errorMessage ?? error?.error?.message);
  return typeof raw === "string" ? raw : "";
};

export const handleErrorMessage = (error: Error) => {
  if ((error as any)?.featureBlocked) return;
  if ((error as any)?.permissionBlocked) return;
  // A signer document call refused until identity verification passes. Not an
  // error to toast: ParticipantScreen listens and swaps to the verification
  // screen. One central place, since every signer catch funnels through here.
  if (getErrorMessageText(error).startsWith(strings.IDV_REQUIRED_PREFIX)) {
    globalEmitter.emit(strings.IDV_REQUIRED_EVENT);
    return;
  }
  const status = error.statusCode || error.status || error.code;
  /**
   * Reason from the API envelope. Backends aren't consistent about the field,
   * so read `message` first, then `errorMessage`, and always fall back to a
   * visible string so the notification is never silently empty.
   */
  const apiMessage = (fallback: string) =>
    (typeof (error as any)?.message === "string" && (error as any).message) ||
    (error as any)?.errorMessage ||
    fallback;
  if (status) {
    switch (status) {
      case 400:
        return openErrorNotification("Please check your request and try again");
      case 401:
        // An embedded widget has no Inkfree login to time out; a 401 there means its session ended.
        return openErrorNotification(
          IS_EMBED
            ? "This embedded session has ended. Ask the host site to open it again."
            : notifiers.LOGGEDOUT,
        );
      case 403:
        return openErrorNotification(
          "You are not authorized to access this resource.",
        );
      case 404:
        return openErrorNotification(
          "The requested resource could not be found.",
        );
      case 429:
        return openErrorNotification(
          "Our servers are currently experiencing a high volume of requests. Please try again in a few minutes.",
        );
      case 500:
        return openErrorNotification(
          "There was an error on the server side. Please try again later.",
        );
      // Billing statuses. Screens that can act on them (add user, reactivate,
      // switch plan) intercept these before they reach here and open the seat
      // prompts; this is the fallback everywhere else, and it must never blame
      // the server for a declined card or a seat conflict.
      case 402:
        return openErrorNotification(
          apiMessage(
            "We couldn't complete the payment. Nothing was charged — please update your payment method and try again.",
          ),
        );
      case 409:
        // Screens that can act on a 409 intercept it and open the keep-picker;
        // this is the fallback, so it can't assume which screen you're on.
        return openErrorNotification(
          apiMessage("This change needs you to choose which users to keep."),
        );
      case 406:
        // 406 carries a human-readable reason (e.g. "The template Test12
        // already exists" or "User limit exceeded").
        return openErrorNotification(
          apiMessage("This action could not be completed. Please try again."),
        );
      default:
        // Any other 4xx is a business error the API explains itself; only a
        // 5xx is genuinely a server fault.
        return openErrorNotification(
          Number(status) >= 400 && Number(status) < 500
            ? apiMessage("Please check your request and try again")
            : "There was an error on the server side. Please try again later.",
        );
    }
  } else if (isNetworkError(error)) {
    return openErrorNotification(NETWORK_ERROR_MESSAGE);
  }
};

export const openErrorNotification = (
  message: any,
  title: string = "Error",
) => {
  globalEmitter.emit(strings.notification, {
    type: strings.error,
    message: message,
    title: title,
  });
};

export function debounce(func: Function, wait: number) {
  let timeout: any;
  return function (...args: any) {
    const context = methodsContext;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), wait);
  };
}

export function debounceEventHandler(func: Function, wait: number) {
  const debounced = debounce(func, wait);
  return function (event: any) {
    event.persist();
    return debounced(event);
  };
}

export const getFormattedNumbers = (value: string) => {
  const matches = value.match(/\d+/g);
  let number = "";
  if (matches !== null) {
    matches.forEach((match) => {
      number += match;
    });
  }
  if (number.length === 10) {
    value = number.replace(/^(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
  }
  return { number: number, maskedNumber: value };
};

export const getFileExtension = (fileName: string) => {
  const ext = /^.+\.([^.]+)$/.exec(fileName);
  return ext === null ? "" : ext[1];
};

export const shortenFileName = (name: string, maxLength = 34): string => {
  if (!name || name.length <= maxLength) return name;

  const dot = name.lastIndexOf(".");
  const extension = dot > 0 && name.length - dot <= 6 ? name.slice(dot) : "";
  const base = extension ? name.slice(0, dot) : name;

  const budget = Math.max(maxLength - extension.length - 1, 8);
  const head = Math.ceil(budget * 0.6);
  const tail = budget - head;

  return `${base.slice(0, head)}…${tail > 0 ? base.slice(-tail) : ""}${extension}`;
};

export const validatePhoneNumber = (mobile: string) => {
  var re = /^\(?(\d{3})\)?[- ]?(\d{3})[- ]?(\d{4})$/;
  return re.test(mobile);
};

export const isPhoneValid = (phone: string) => {
  try {
    const phoneUtil = PhoneNumberUtil.getInstance();
    return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
  } catch (error: any) {
    return false;
  }
};

/**
 * Split object the backend expects for a phone number (SMS delivery / phone
 * authentication): the dial code and the national number kept separate.
 */
export interface PhoneParts {
  countryCode: string;
  number: string;
}

/**
 * Parse a display phone string (what `CustomContactNumberInput` emits, e.g.
 * `"+1 5551234567"`) into `{ countryCode, number }` for the backend. Returns
 * `undefined` for empty input or a dial-code-only value (e.g. the `"+1 "` the
 * control emits after a bare country pick), so callers can omit the field.
 *
 * Idempotent on purpose: a phone loaded from the API is *already* the split
 * object, and not every screen hydrates it back to a string before saving. Such
 * a value passes straight through rather than reaching `.trim()` and throwing
 * `raw.trim is not a function` — which would take down the whole send, since
 * this runs while the request body is being built.
 */
export const toPhoneParts = (
  raw?: string | PhoneParts | null,
): PhoneParts | undefined => {
  if (raw && typeof raw === "object") {
    const number = (raw.number || "").trim();
    return number ? { countryCode: raw.countryCode || "", number } : undefined;
  }
  const value = (raw || "").trim();
  if (!value) return undefined;
  try {
    const phoneUtil = PhoneNumberUtil.getInstance();
    const parsed = phoneUtil.parseAndKeepRawInput(value);
    const national = parsed.getNationalNumber()?.toString() ?? "";
    if (!national) return undefined;
    return { countryCode: `+${parsed.getCountryCode()}`, number: national };
  } catch {
    // Fallback: "+<dialCode> <rest>" — keep only the digits of the rest.
    const match = value.match(/^\+(\d{1,3})[\s-]*(.*)$/);
    const number = (match?.[2] ?? "").replace(/\D/g, "");
    if (!match || !number) return undefined;
    return { countryCode: `+${match[1]}`, number };
  }
};

/**
 * Rebuild the display string (`"+1 5551234567"`) from the backend's split
 * object so `CustomContactNumberInput` can seed itself when an existing
 * request / draft / template is loaded. A plain string passes through unchanged.
 */
export const fromPhoneParts = (
  parts?: PhoneParts | string | null,
): string => {
  if (!parts) return "";
  if (typeof parts === "string") return parts;
  const number = (parts.number || "").trim();
  if (!number) return "";
  return `${parts.countryCode || ""} ${number}`.trim();
};

export const tableRowRightAlign = (value: any) => {
  return (
    <Box component="span" sx={{ textAlign: "right", display: "block" }}>
      {value}
    </Box>
  );
};

const doesResourcesExistInLocalStorage = (resources: Resources[]) => {
  if (!isTruthy(resources)) {
    store.dispatch(logOutAction({}));
    history.push(viewpaths.landingViewPath);
    return false;
  }
  return true;
};

export const convertResourceToObjectFormat = (resources: Resources[]) => {
  if (!doesResourcesExistInLocalStorage(resources)) {
    return {} as FormattedResources;
  }
  let returnObject = {} as { [key: string]: string[] };
  resources.forEach((resource) => {
    returnObject[resource.path] = resource.permissions;
  });
  return returnObject;
};

/**
 * The backend sends every timestamp in **UTC** (it used to send Eastern time —
 * the `EST` in this name is historical, kept so ~60 call sites don't churn).
 * The wire format is unchanged; only the zone it is read in.
 */
export const convertESTtoUserLocalTime = (
  inputTime: string,
  inputTimeFormat = "MM-DD-YYYY HH:mm:ss",
  outputTimeFormat = "MMMM D, YYYY h:mm A",
) => {
  const userTimeZone = MomentHelpers.guessTheTimeZone();
  const inputTimezone = BACKEND_TIMEZONE;
  return moment
    .tz(inputTime, inputTimeFormat, inputTimezone)
    .tz(userTimeZone)
    .format(outputTimeFormat);
};

export const formatDateToStartOfDay = (dateStr: string) => {
  const date = new Date(dateStr);
  date.setHours(0, 0, 0, 0);

  return date.toLocaleString("sv-SE").replace(" ", "T");
};

export const formatDateToEndOfDay = (dateStr: string) => {
  const date = new Date(dateStr);
  date.setHours(23, 59, 59, 999);

  return date.toLocaleString("sv-SE").replace(" ", "T");
};

export const getABResultAfterType = (
  resultsAfterInterval: number,
  resultsAfterType: string,
) => {
  if (resultsAfterInterval === 1 && resultsAfterType === "hours") {
    return "1 hour";
  } else if (resultsAfterInterval === 1 && resultsAfterType === "days") {
    return "1 day";
  } else {
    return `${resultsAfterInterval} ${resultsAfterType.toLowerCase()}`;
  }
};

export const getTentativeResultTime = (
  resultTentativeTime: any,
  resultsAfterInterval: number,
  resultsAfterType: string,
) => {
  if (!resultTentativeTime) {
    return null;
  }
  const resultTime = moment(resultTentativeTime, "MMM DD, YYYY hh:mm a");
  const type = resultsAfterType as DurationInputArg2;

  return moment(resultTime.add(resultsAfterInterval, type)).format(
    "MMM DD, YYYY hh:mm a",
  );
};

// export function isValidEmail(email: string = "") {
//   return strings.EmailRegex.test(email);
// }

// export function isPasswordStrong(password: string = "") {
//   return strings.PasswordRegex.test(password);
// }

export function isValidOTP(otp: string = "", digits: number = 6) {
  return otp.length === digits;
}

export function checkPasswordStrength(password: string = "") {
  let percentage = 0;
  // Check minimum length
  if (password.length >= 8) {
    percentage += 25;
  }
  // Check for at least one lowercase letter
  if (/[a-z]/.test(password)) {
    percentage += 25;
  }
  // Check for at least one number
  if (/\d/.test(password)) {
    percentage += 25;
  }
  // // Check for at least one special character
  // if (/[!@#$%^&*.?]/.test(password)) {
  //   percentage += 25;
  // }
  // Check for at least one uppercase letter
  if (/[A-Z]/.test(password)) {
    percentage += 25;
  }

  // Determine strength based on percentage
  const strength = percentage === 100 ? "Strong" : "Weak";
  return { strength, percentage };
}

export function formatDateInLongDateFormat(date: Date | string) {
  return moment(date).format("LL");
}

export function formatDateWithTimeInLongDateFormat(date: Date | string) {
  return moment(date).format("MMMM D, YYYY");
}

export const convertPriceToDollarFormat = (value: number) => {
  return `$${value.toFixed(2)}`;
};

export const getPhoneNumberWithCountryCode = (number: string) => {
  const countryCode = number.substring(1, number.indexOf(" "));
  const phoneNumber = number
    .substring(number.indexOf(" "))
    .split("")
    .filter(
      (char: string) =>
        char !== " " && char !== "(" && char !== ")" && char !== "-",
    )
    .join("");

  const formattedObject = {
    countryCode: parseInt(countryCode),
    type: "Mobile",
    value: parseInt(phoneNumber),
  };

  return formattedObject;
};

export const formattedPhoneNumber = (number: string) => {
  const formattedNumber: string = number.replace(
    /(\d{3})(\d{3})(\d{4})/,
    "($1) $2-$3",
  );
  return formattedNumber;
};

export const addHours = (timeObject: any, field?: string): string => {
  let addedMinutes = 0;

  if (!field) {
    Object.keys(timeObject).forEach((key) => {
      const fieldValue = timeObject[key];
      const [hours, minutes] = fieldValue.split(":").map(Number);
      addedMinutes += hours * 60 + minutes;
    });
  } else {
    timeObject.forEach((currObj: any) => {
      const fieldValue = currObj[field];
      const [hours, minutes] = fieldValue.split(":").map(Number);
      addedMinutes += hours * 60 + minutes;
    });
  }

  const resultHours = Math.floor(addedMinutes / 60);
  const resultMinutes = addedMinutes % 60;
  return `${resultHours.toString().padStart(2, "0")}:${resultMinutes
    .toString()
    .padStart(2, "0")}`;
};

export const addDecimalHours = (timeObject: any, field?: string): string => {
  let totalDecimalHours = 0;

  const convertToDecimalAndReplace = (time: string): number => {
    return parseFloat(time.replace(":", "."));
  };

  if (!field) {
    Object.keys(timeObject).forEach((key) => {
      const fieldValue = timeObject[key];
      totalDecimalHours += convertToDecimalAndReplace(fieldValue);
    });
  } else {
    timeObject.forEach((currObj: any) => {
      const fieldValue = currObj[field];
      totalDecimalHours += convertToDecimalAndReplace(fieldValue);
    });
  }

  const totalDecimal = totalDecimalHours.toFixed(2);
  const [integerPart, decimalPart] = totalDecimal.split(".");

  return `${integerPart.padStart(2, "0")}:${decimalPart.padStart(2, "0")}`;
};

export const stringToFile = (url: string, fileName: string): File => {
  const byteString = atob(url.split(",")[1]);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);

  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }

  return new File([ab], fileName, { type: "image/png" });
};

export const capitalizeFirstLetter = (word: string) => {
  return word.charAt(0).toUpperCase() + word.slice(1);
};

/**
 * Up to two uppercase initials for an avatar fallback ("Devarsh Panchal" → "DP",
 * "devarsh.panchal@softsages.com" → "D"). Returns an empty string when there is
 * nothing usable so callers can decide what to render instead.
 */
export const getNameInitials = (name?: string) => {
  const words = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "";
  const initials = words.slice(0, 2).map((word) => word.charAt(0));
  return initials.join("").toUpperCase();
};

export const splitByCamelCase = (str: string) => {
  const formatted = str
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
    .trim();
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
};

export const calculateRelativeTime = (inputTime: string): string => {
  const userTimeZone = moment.tz.guess();
  // Parsed in the backend's zone (UTC) before being shown relative to the
  // reader's own clock — otherwise "2 hours ago" is off by the UTC offset.
  const providedDate = moment.tz(inputTime, BACKEND_TIMEZONE);
  const localTime = providedDate.clone().tz(userTimeZone);
  return localTime.fromNow();
};

export const formatToCurrency = (value: number, currency: string = "USD") => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency,
  }).format(value);
};

export const formatCount = (value: number) => {
  return new Intl.NumberFormat("en-US").format(value);
};

// Plan limits at or above this value are treated as "unlimited" (the backend
// uses a large sentinel for unlimited plans). Kept consistent with the
// existing certified-sign display in the drawer TopNav.
export const UNLIMITED_LIMIT_THRESHOLD = 9999;

export const isUnlimitedLimit = (value: number | null | undefined): boolean =>
  (value ?? 0) >= UNLIMITED_LIMIT_THRESHOLD;

// Formats a plan limit, showing "Unlimited" for sentinel/unlimited values and
// a thousands-separated number otherwise.
export const formatLimit = (value: number | null | undefined): string =>
  isUnlimitedLimit(value) ? "Unlimited" : formatCount(value ?? 0);

export const trimValue = (value: string) => {
  return value.trim();
};

export const formatAddress = (address: {
  line1: string;
  line2: string;
  city: string;
  state: string;
  country: string;
  pinCode: string;
}) => {
  const addressComponents = [
    address.line1,
    address.line2,
    address.city,
    address.state,
    address.country,
    address.pinCode,
  ];
  return addressComponents
    .filter((component) => isTruthy(component))
    .join(", ");
};

export const blockInvalidChar = (event: any) =>
  ["e", "E", "+", "-"].includes(event.key) && event.preventDefault();

export const isDateBetweenTwoDates = (
  date: string,
  startDate: string,
  endDate: string,
) => {
  return moment(date).isBetween(
    moment(startDate),
    moment(endDate),
    undefined,
    "[]",
  );
};

export const handleWheelEvent = (event: React.WheelEvent<HTMLInputElement>) => {
  const inputElement = event.target as HTMLInputElement;
  inputElement.blur();
  setTimeout(() => inputElement.focus(), 0);
  event.preventDefault();
};

export const getUserAgentDetails = (userAgent: string) => {
  if (isTruthy(userAgent)) {
    const parser = new UAParser(userAgent);
    const result = parser.getResult();

    const browserName = result.browser.name || "Unknown Browser";
    const browserVersion = result.browser.version || "Unknown Version";

    const osName = result.os.name || "Unknown OS";

    const deviceType = result.device.type
      ? result.device.type
      : "Desktop/Laptop";

    return `${browserName} V${browserVersion} [${deviceType}, ${osName}]`;
  } else {
    return "-";
  }
};

export const ExportCSV = (headers: any, title: any, defaultEmail: string) => {
  const convertToCSV = () => {
    let str = "";
    str += getHeaderData();
    str += getDefaultRowData();
    return str;
  };

  const getHeaderData = () => {
    let str = "";
    headers.forEach((header: any) => {
      if (str !== "") str += ",";
      str += header;
    });
    str += "\r\n";
    return str;
  };

  const getDefaultRowData = () => {
    return "default@example.com,John,Doe,+1 (123) 456 7891,201-Test 123 Main St,12345,New York,New York,USA,Tag,EST\r\n";
  };

  const exportCSVFile = () => {
    let csv = convertToCSV();
    let exportedFilename = title + ".csv" || "export.csv";

    let blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

    let link = document.createElement("a");
    if (link.download !== undefined) {
      let url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", exportedFilename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return exportCSVFile;
};

export default ExportCSV;

export const capitalizeEntity = (entity: string) => {
  return entity
    .replace(/([a-z])([A-Z])/g, "$1 $2") // Add space before uppercase letters
    .replace(/^./, (str) => str.toUpperCase()); // Capitalize the first letter
};

export const copyTextHandler = async (requiredValue: string) => {
  try {
    await navigator.clipboard.writeText(requiredValue);
    openSuccessNotification("Your text has been copied successfully!");
  } catch (error: any) {
    openErrorNotification(
      isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR,
    );
  }
};

/** Backend timestamps are UTC — see `convertESTtoUserLocalTime` on the name. */
export const convertESTtoUserLocalDateAndTime = (
  inputTime: string,
  outputTimeFormat: string = "MMM DD, YYYY hh:mm a",
  inputTimeFormat: string = "MM/DD/YYYY HH:mm:ss",
) => {
  const userTimeZone = MomentHelpers.guessTheTimeZone();
  const inputTimezone = BACKEND_TIMEZONE;
  return moment
    .tz(inputTime, inputTimeFormat, inputTimezone)
    .tz(userTimeZone)
    .format(outputTimeFormat);
};

export const setFileName = (fileName: string) => {
  let filenameUpdated = fileName?.split("/").pop();
  return filenameUpdated;
};

/**
 * Local time -> the zone the backend expects (UTC). Has no call sites today —
 * the three date pickers each inline the same conversion — but it is retained,
 * and retargeted off Eastern, so nothing picks it up later and sends the wrong
 * zone back.
 */
export const convertToEstTime = (
  dateTime: any,
  outputFormat: string = "MM/DD/yyyy HH:mm:ss",
) => {
  return moment(dateTime).tz(BACKEND_TIMEZONE).format(outputFormat);
};

export function formatDateInLongDateTimeFormat(
  date: Date | string,
  outputFormat: string = "MMMM D, YYYY [at] h:mm A",
) {
  // Read as UTC, rendered in the reader's zone. This previously parsed the
  // backend's string as browser-local, which was already wrong for anyone
  // outside the server's zone — the UTC switch is the moment to correct it.
  const dateAndTime = moment.utc(date, "MM/DD/YYYY HH:mm:ss").local();
  const formattedDate = dateAndTime.format(outputFormat);
  return formattedDate;
}

export function formateRelativeTimeFormat(date: Date | string) {
  // Same correction as `formatDateInLongDateTimeFormat` above.
  const dateAndTime = moment.utc(date, "MM/DD/YYYY HH:mm:ss").local();
  const formattedDate = dateAndTime.fromNow();
  return formattedDate;
}

export const timeZones = moment.tz.names().map((tz) => {
  const offsetMinutes = moment.tz(tz).utcOffset(); // Offset in minutes
  const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60); // Get the hours
  const offsetMinutesRemainder = Math.abs(offsetMinutes) % 60; // Get the remaining minutes
  const formattedOffset = `(GMT${offsetMinutes >= 0 ? "+" : "-"}${offsetHours
    .toString()
    .padStart(2, "0")}:${offsetMinutesRemainder.toString().padStart(2, "0")})`;

  return {
    label: `${formattedOffset} ${tz.replace("_", " ")}`, // Display exact offset
    value: tz,
  };
});

export const validatePageQuery = (pageFromUrl: any) => {
  if (!pageFromUrl || isNaN(+pageFromUrl) || +pageFromUrl <= 0) {
    return 1;
  }
  return +pageFromUrl;
};

export const validatePerPageQuery = (perPageFromUrl: any) => {
  if (!perPageFromUrl || isNaN(+perPageFromUrl) || +perPageFromUrl < 10) {
    return 10;
  } else if (+perPageFromUrl > 100) {
    return 100;
  } else if (+perPageFromUrl % 5 !== 0) {
    return +perPageFromUrl - (+perPageFromUrl % 5);
  }
  return +perPageFromUrl;
};

export const validateTypeOrStatusQuery = (fromUrl: any) => {
  if (!["-1", "0", "1"].includes(fromUrl)) {
    return "-1";
  }
  return fromUrl;
};

export const initialPageFilterState = (
  searchText: string,
  page?: number,
  rowsPerPage?: number,
  dropdownValue?: string,
  status?: string,
) => {
  return {
    searchText,
    page,
    rowsPerPage,
    dropdownValue,
    status,
  };
};

export const getDateInterval = (filter: string, dateRange: DateRange) => {
  let fromDate = dateRange.fromDate;
  let toDate = dateRange.toDate;

  switch (filter) {
    case strings.currentWeek:
      fromDate = new Date(moment().startOf("week").format("MM-DD-yyyy"));
      toDate = new Date(moment().format("MM-DD-yyyy"));
      break;
    case strings.previousWeek:
      fromDate = new Date(
        moment().subtract(1, "weeks").startOf("week").format("MM-DD-yyyy"),
      );
      toDate = new Date(
        moment().subtract(1, "weeks").endOf("week").format("MM-DD-yyyy"),
      );
      break;
    case strings.lastSevenDays:
      fromDate = new Date(moment().subtract(6, "day").format("MM-DD-yyyy"));
      toDate = new Date(moment().format("MM-DD-yyyy"));
      break;
    case strings.currentMonth:
      fromDate = new Date(moment().startOf("month").format("MM-DD-yyyy"));
      toDate = new Date(moment().format("MM-DD-yyyy"));
      break;
    case strings.previousMonth:
      fromDate = new Date(
        moment().subtract(1, "month").startOf("month").format("MM-DD-yyyy"),
      );
      toDate = new Date(
        moment().subtract(1, "month").endOf("month").format("MM-DD-yyyy"),
      );
      break;
    case strings.lastThirtyDays:
      fromDate = new Date(moment().subtract(29, "day").format("MM-DD-yyyy"));
      toDate = new Date(moment().format("MM-DD-yyyy"));
      break;
    case strings.allTime:
      fromDate = null;
      toDate = null;
      break;
    default:
      fromDate = dateRange.fromDate;
      toDate = dateRange.toDate;
  }
  return {
    fromDate,
    toDate,
  };
};

export const last30DaysFromDate = moment()
  .subtract(29, "days")
  .format("YYYY-MM-DD");
export const last30DaysToDate = moment().format("YYYY-MM-DD");

export const calculateDaysBetween = (dateRange: DateRange) => {
  if (dateRange.fromDate && dateRange.toDate) {
    return moment(dateRange.toDate).diff(moment(dateRange.fromDate), "days");
  }
  return 0;
};

export const cleanInput = (value: string) => {
  return value.replace(/^\s+/, "").replace(/\s{2,}/g, " ");
};

export function isPasswordStrong(password: string = "") {
  return strings.PasswordRegex.test(password);
}

export const capitalizeText = (inputString: string): string => {
  return inputString.replace(/^./, (char) => char.toUpperCase());
};

export const javaToMomentFormat = (s: string): string => {
  if (!s || typeof s !== "string") return s;
  return s.replace(/yyyy/g, "YYYY").replace(/yy/g, "YY").replace(/dd/g, "DD");
};

export const momentToJavaFormat = (s: string): string => {
  if (!s || typeof s !== "string") return s;
  return s.replace(/YYYY/g, "yyyy").replace(/YY/g, "yy").replace(/DD/g, "dd");
};

export const renderTooltipText = (text: string, length: number = 50) => (
  <Tooltip title={text} arrow>
    <span>
      {text?.length > length ? text?.slice(0, length) + "..." : (text ?? "")}
    </span>
  </Tooltip>
);

export const getStatusColor = (status: string) => {
  switch (status) {
    case "Completed":
      return {
        color: "#47AA12",
        backgroundColor: "#F7FFF2",
      };
    case "Submitted":
      return {
        color: "#FF9436",
        backgroundColor: "#FFF6E2",
      };
    case "Draft":
      return {
        color: "#475569",
        backgroundColor: "#EFEEEA",
      };
    case strings.voided:
      return {
        color: "#c23118",
        backgroundColor: "#fff2f2",
      };
    case "InProgress":
      return {
        color: "#125DDB",
        backgroundColor: "#E8EFFC",
      };
    case "Rejected":
      return {
        color: "#c23118",
        backgroundColor: "#fff2f2",
      };
    case strings.declined:
      return {
        color: "#c23118",
        backgroundColor: "#fff2f2",
      };
    case "InReview":
      return {
        color: "#00B2FF",
        backgroundColor: "#00B2FF0F",
      };
    default:
      return {
        color: "#6B91FC",
        backgroundColor: "#F3F7FF",
      };
  }
};

export const convertEstToLocalTimezone = (
  dateTimeString: string,
  incomingFormat: string,
  allowPastDate: boolean = true,
) => {
  const presentMoment = moment().toISOString();
  const localTimeZone = moment.tz.guess(true);
  const dateTime = moment.tz(dateTimeString, incomingFormat, BACKEND_TIMEZONE);
  if (!allowPastDate && dateTime.isBefore(presentMoment)) {
    return moment("Invalid Date");
  } else {
    return dateTime.tz(localTimeZone);
  }
};

export const isOfferActive = () => {
  if (!strings.OFFER_BANNER_LIVE) return false;
  const currentDate = new Date();
  return (
    currentDate >= strings.OFFER_START_DATE &&
    currentDate <= strings.OFFER_END_DATE
  );
};

// `DialogFooter` used to live here: a two-button footer every dialog pasted
// into `CustomDialog`. The Modal System's footer bar replaces it — pass
// `primaryLabel` / `cancelLabel` to `AppModal`, or its `footer` slot when a
// screen genuinely needs different actions.
